import { publicClient as client } from "../../api/publicClient";
import { API_ROUTES } from "../../api/endpoints";


// ===== Types =====

export interface HeroWord {
  id: number;
  word: string;
  displayOrder: number;
}

export interface HeroContent {
  headingLine1: string;
  headingLine2: string;
  paragraph: string;
  rotatingWords: HeroWord[];
  updatedAt?: string;
}

export interface ChallengePair {
  id: number;
  challengeTitle: string;
  challengeDescription: string;
  solutionTitle: string;
  solutionDescription: string;
  displayOrder: number;
}

export interface ChallengesSection {
  headingLine1: string;
  headingLine2: string;
  subtitle: string;
  pairs: ChallengePair[];
  updatedAt?: string;
}

export interface PricingFeature {
  id: number;
  featureText: string;
  displayOrder: number;
}

export interface PricingTier {
  id: number;
  strengthLabel: string;
  serverCost: number;
  maintenanceCost: number;
  totalAnnualCost: number;
  displayOrder: number;
}

export interface PricingSection {
  cardTitle: string;
  perStudentCost: number;
  costLabel: string;
  renewalHeading: string;
  gstPercentage: number;
  features: PricingFeature[];
  tiers: PricingTier[];
  updatedAt?: string;
}

export interface TestimonialItem {
  id: number;
  quote: string;
  rating: number;
  authorName: string;
  authorTitle: string;
  authorSchool: string;
  logoUrl: string;
  displayOrder: number;
}

export interface TestimonialsSection {
  badgeText: string;
  headingLine1: string;
  headingLine2: string;
  subtitle: string;
  testimonials: TestimonialItem[];
  updatedAt?: string;
}

export interface FaqItem {
  id: number;
  question: string;
  answer: string;
  displayOrder: number;
}

export interface BlogPostSummary {
  slug: string;
  title: string;
  excerpt: string;
  category: string | null;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  readTimeMinutes: number;
  publishedAt: string;
}
export interface BlogCategory {
  name: string;
  count: number;
}
export interface BlogPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface BlogListResponse {
  posts: BlogPostSummary[];
  pagination: BlogPagination;
}

export interface BlogPostDetail {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  coverImageUrl: string;
  coverImageAlt: string | null;
  readTimeMinutes: number;
  publishedAt: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  canonicalUrl: string | null;
  seoHeadHtml?: string | null;
}

export interface ProblemItem {
  id?: number;
  icon: string;
  title: string;
  text: string;
}

export interface StatItem {
  id?: number;
  icon: string;
  val: string;
  label: string;
}

export interface ModuleItem {
  id?: number;
  icon: string;
  color: string;
  name: string;
  text?: string;
}

export interface TrustItem {
  id?: number;
  icon: string;
  val: string;
  label: string;
}

export interface PublicLeadPayload {
  instituteName: string;
  contactPerson: string;
  contactEmail: string;
  contactPhone?: string;
  message?: string;
  source: "CONTACT_FORM" | "DEMO_REQUEST";
}

// ===== Service =====
// Route strings now come from the single global API_ROUTES source
// (src/api/endpoints.ts) instead of a local, duplicated PATHS object.

export const landingPageService = {
  // Public lead capture — used by the Contact and Request-a-Demo modals.
  submitLead: async (payload: PublicLeadPayload): Promise<void> => {
    await client.post(API_ROUTES.LEADS_PUBLIC, payload);
  },

  // Hero
  getHero: async (): Promise<HeroContent> => {
    const res = await client.get<{ hero: HeroContent }>(API_ROUTES.CMS_HERO);
    return res.data.hero;
  },
  updateHero: async (payload: HeroContent): Promise<HeroContent> => {
    const res = await client.put(API_ROUTES.CMS_HERO, payload);
    return res.data;
  },

  getChallenges: async (): Promise<ChallengesSection> => {
    const res = await client.get<{ section: ChallengesSection }>(API_ROUTES.CMS_CHALLENGES);
    return res.data.section;
  },

  // Pricing
  getPricing: async (): Promise<PricingSection> => {
    const res = await client.get<{ section: PricingSection }>(API_ROUTES.CMS_PRICING);
    return res.data.section;
  },

  getTestimonials: async (): Promise<TestimonialsSection> => {
    const res = await client.get<{ section: TestimonialsSection }>(API_ROUTES.CMS_TESTIMONIALS);
    return res.data.section;
  },

  getSocialLinks: async (): Promise<{ id: number; platform: string; url: string }[]> => {
    const res = await client.get<{ links: { id: number; platform: string; url: string }[] }>(API_ROUTES.CMS_SOCIAL_LINKS);
    return res.data.links;
  },

  getFaqs: async (): Promise<FaqItem[]> => {
    const res = await client.get<{ faqs: FaqItem[] }>(API_ROUTES.CMS_FAQ);
    return res.data.faqs;
  },

  // Blog
  getBlogPosts: async (page = 1, limit = 12, filter: { search?: string; category?: string } = {}): Promise<BlogListResponse> => {
    const res = await client.get<BlogListResponse>(API_ROUTES.CMS_BLOG, { params: { page, limit, search: filter.search || undefined, category: filter.category || undefined } });
    return res.data;
  },

  getBlogCategories: async (): Promise<BlogCategory[]> => {
    const res = await client.get<{ categories: BlogCategory[] }>(`${API_ROUTES.CMS_BLOG}/categories`);
    return res.data.categories;
  },

   getBlogPostBySlug: async (slug: string): Promise<BlogPostDetail> => {
    const res = await client.get<{ post: BlogPostDetail }>(`${API_ROUTES.CMS_BLOG}/${slug}`);
    return res.data.post;
  },

  getCmsPages: async (): Promise<{ slug: string; title: string; coverImageUrl?: string | null; coverImageAlt?: string | null }[]> => {
    const res = await client.get(API_ROUTES.CMS_PAGES);
    return res.data.pages;
  },

  getCmsPageBySlug: async (slug: string) => {
    const res = await client.get(`${API_ROUTES.CMS_PAGES}/${encodeURIComponent(slug)}`);
    return res.data.page;
  },

  // Problems
  getProblems: async (): Promise<ProblemItem[]> => {
    const res = await client.get(API_ROUTES.CMS_PROBLEMS);
    return res.data;
  },
  updateProblems: async (items: ProblemItem[]): Promise<ProblemItem[]> => {
    const res = await client.put(API_ROUTES.CMS_PROBLEMS, { items });
    return res.data;
  },

  // Stats
  getStats: async (): Promise<StatItem[]> => {
    const res = await client.get(API_ROUTES.CMS_STATS);
    return res.data;
  },
  updateStats: async (items: StatItem[]): Promise<StatItem[]> => {
    const res = await client.put(API_ROUTES.CMS_STATS, { items });
    return res.data;
  },

  // Modules
  getModules: async (): Promise<ModuleItem[]> => {
    const res = await client.get(API_ROUTES.CMS_MODULES);
    return res.data;
  },
  updateModules: async (items: ModuleItem[]): Promise<ModuleItem[]> => {
    const res = await client.put(API_ROUTES.CMS_MODULES, { items });
    return res.data;
  },

  // Trust bar
  getTrustBar: async (): Promise<TrustItem[]> => {
    const res = await client.get(API_ROUTES.CMS_TRUST_BAR);
    return res.data;
  },
  updateTrustBar: async (items: TrustItem[]): Promise<TrustItem[]> => {
    const res = await client.put(API_ROUTES.CMS_TRUST_BAR, { items });
    return res.data;
  },
};